import React from "react";
import { pdfjs } from "react-pdf";
import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { backendUrl, customerName, apiVersion } from "./constants/constants";
import PropTypes from "prop-types";
import { Tabs, Tab, Box, Button, Alert } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import SearchForm from "./components/Inputs/SearchForm";
import PositionDataTable from "./components/DataDisplay/PositionDataTable";
import EmployeeCard from "./components/Surface/EmployeeCard";
import TransactionAlert from "./components/Feedback/TransactionAlert";
import UnauthorizedPopup from "./components/Feedback/UnauthorizedPopup";
import dots from "./images/dots-loading.gif";
import { backendUrls } from "./constants/constants";
import { calculateRemainingDays } from "./utils/calculateRemainingDays";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2, pb: 2 }}>{children}</Box>}
    </div>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const headerContainer = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100px",
};

const imageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const logoStyle = {
  width: "auto",
  height: "50px",
  position: "absolute",
};

const Permission = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { applicationId, language } = useParams();
  const [currentAssignments, setCurrentAssignments] = useState([]);
  const [historyAssignments, setHistoryAssignments] = useState([]);
  const token = auth.user?.id_token;
  const [applicationDetail, setApplicationDetail] = useState({});
  const [searchResult, setSearchResult] = useState([]);
  const [alert, setAlert] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);
  const [value, setValue] = useState(0);
  const [currentLoading, setCurrentLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [moreResultLoading, setMoreResultLoading] = useState(false);
  const [displayError, setDisplayError] = useState({
    display: false,
    title: "",
    description: "",
  });
  const [alertType, setAlertType] = useState("");
  const [message, setMessage] = useState("");
  const [showCurrentAssignments, setShowCurrentAssignments] = useState(true);
  const [showSearchResult, setShowSearchResult] = useState(true);
  const [FRONTEND, setFRONTEND] = useState({});
  const configUrl = backendUrls.configUrl;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchFormData, setSearchFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    skip: 0,
  });

  let customerLogo;
  try {
    customerLogo = FRONTEND.Theming.customerAssets.logo;
  } catch {}

  let customerHeader;
  try {
    customerHeader = FRONTEND.Theming.customerAssets.header;
  } catch {}

  if (FRONTEND?.Theming?.Images?.FavIcon) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href = FRONTEND.Theming.Images.FavIcon;
  }

  useEffect(() => {
    if (!auth.isAuthenticated) {
      sessionStorage.setItem("beforeLogin", location.pathname);
      navigate("/callback");
      return;
    }
    fetchApplicationDetails();
    fetchCurrentAssignmentData();
    fetchHistoryAssignmentData();
    fetchConfigTheming();
  }, [token, applicationId]);

  const fetchApplicationDetails = async () => {
    try {
      const apiUrl = `${backendUrl}/${apiVersion}/jobApplicationDetails`;
      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          applicationId: applicationId,
          customerName: customerName,
          Authorization: `Bearer ${token}`,
        },
      });
      setApplicationDetail(response.data);
    } catch (error) {
      // throw the error message as per the error code returned from backend
      if (error.response.status === 401) {
        setDisplayError({
          display: true,
          title: t("unauthorizedPopup.title"),
          description: t("unauthorizedPopup.description"),
        });
      } else if (error.response.status === 403) {
        setDisplayError({
          display: true,
          title: t("forbiddenPopup.title"),
          description: t("forbiddenPopup.description"),
        });
      } else if (error.response.status === 478) {
        setDisplayError({
          display: true,
          title: t("anonymizedPopup.title"),
          description: t("anonymizedPopup.description"),
        });
      } else if (error.response.status === 500) {
        setDisplayError({
          display: true,
          title: t("serverErrorPopup.title"),
          description: t("serverErrorPopup.description"),
        });
      } else {
        setDisplayError({
          display: true,
          title: t("genericErrorPopup.title"),
          description: t("genericErrorPopup.description"),
        });
      }
    }
  };

  const fetchCurrentAssignmentData = async () => {
    const apiURL = `${backendUrl}/${apiVersion}/currentAssignments`;
    setCurrentLoading(true);
    try {
      const response = await axios.get(apiURL, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          applicationId: applicationId,
          customerName: customerName,
        },
      });
      setCurrentAssignments(response.data.results);
    } catch (error) {
      console.log("error", error);
    } finally {
      setCurrentLoading(false);
    }
  };

  const fetchHistoryAssignmentData = async () => {
    const apiURL = `${backendUrl}/${apiVersion}/assignmentHistory`;
    setHistoryLoading(true);
    try {
      const response = await axios.get(apiURL, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          applicationId: applicationId,
          customerName: customerName,
        },
      });
      setHistoryAssignments(response.data.results);
    } catch (error) {
      console.log("error", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleChange = (newValue) => {
    setValue((prevState) => (prevState === 0 ? 1 : 0));
  };

  const handleAlert = (value, type = "success") => {
    setAlert(value);
    setAlertType(type);
    type === "success"
      ? setMessage(t("alertTransactions.grantAccessMessage"))
      : setMessage(t("alertTransactions.revokeAccessMessage"));
  };

  const handleCurrentAssignment = (value) => {
    setShowCurrentAssignments(value);
    setShowSearchResult(false);
  };

  const handleUserSearch = async (formData) => {
    setSearchFormData(formData);
    setResultLoading(true);
    const stringifyData = JSON.stringify(formData);
    try {
      const apiURL = `${backendUrl}/${apiVersion}/userSearch`;
      const response = await axios.post(apiURL, stringifyData, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          customerName: customerName,
          applicationId: applicationId,
        },
      });
      setSearchResult(response.data.results);
    } catch (error) {
      console.log("error:", error);
    } finally {
      setResultLoading(false);
      setShowSearchResult(true);
      setShowCurrentAssignments(false);
    }
  };

  const handleLoadMoreUser = async () => {
    try {
      setMoreResultLoading(true);
      const apiURL = `${backendUrl}/${apiVersion}/userSearch`;
      searchFormData.skip +=
        FRONTEND?.UserSearch?.defaultUserSearchOptions?.pageSize || 10;
      const response = await axios.post(
        apiURL,
        JSON.stringify(searchFormData),
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            customerName: customerName,
            applicationId: applicationId,
          },
        }
      );
      setSearchResult((searchResult) => [
        ...searchResult,
        ...response.data.results,
      ]);
    } catch (error) {
      console.log("error:", error);
    } finally {
      setMoreResultLoading(false);
    }
  };

  const handleRender = () => {
    fetchCurrentAssignmentData();
    fetchHistoryAssignmentData();
  };

  const fetchConfigTheming = () => {
    const customerName =
      sessionStorage.getItem("customerName") ||
      window.location.host.split(".")[0];
    fetch(configUrl + `?language=${language}&customerName=${customerName}`)
      .then((response) => response.json())
      .then((data) => setFRONTEND(data));
  };

  return (
    <Box
      sx={{
        maxWidth: "1800px",
        margin: "0 auto",
      }}
    >
      <Box>
        <Box style={headerContainer}>
          <img src={customerLogo} alt="header" style={logoStyle} />
          <img src={customerHeader} alt="header" style={imageStyle} />
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Permissions Tab"
            centered
          >
            <Tab
              label={t("navigationTabs.grantPermissionTab")}
              {...a11yProps(0)}
              sx={{ fontWeight: "bold" }}
            />
            <Tab
              label={t("navigationTabs.historyTab")}
              {...a11yProps(1)}
              sx={{ fontWeight: "bold" }}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <UnauthorizedPopup
            open={displayError.display}
            title={displayError.title}
            description={displayError.description}
          />
          <SearchForm
            triggerUserSearch={handleUserSearch}
            triggerCurrentAssignment={handleCurrentAssignment}
            resultLoading={resultLoading}
            configuration={FRONTEND}
          />
          <PositionDataTable
            firstName={applicationDetail.firstName}
            lastName={applicationDetail.lastName}
            jobTitle={applicationDetail.externalTitle}
            jobReqId={applicationDetail.jobReqId}
            applicationId={applicationId}
            configuration={FRONTEND}
          />
          {alert && (
            <TransactionAlert
              message={message}
              alertType={alertType}
              open={alert}
              handleAlert={handleAlert}
            />
          )}
          <Grid container spacing={2} mt={2} p={3}>
            {/* display current assignments */}
            {showCurrentAssignments &&
              currentAssignments.map((user, key) => {
                const { firstName, lastName, department, email } =
                  user.cust_assignedUserNav;
                const daysLeft = calculateRemainingDays(user.cust_endDate);

                return (
                  daysLeft > 0 && (
                    <Grid
                      item
                      size={{ xs: 12, sm: 12, md: 6, lg: 4 }}
                      key={key}
                    >
                      <EmployeeCard
                        firstName={firstName}
                        lastName={lastName}
                        department={department}
                        userName={user.cust_assignedUser}
                        email={email}
                        token={token}
                        applicationId={applicationId}
                        handleAlert={handleAlert}
                        expiryDate={user.cust_endDate}
                        startDate={user.effectiveStartDate}
                        handleRender={handleRender}
                        daysLeft={daysLeft}
                      />
                    </Grid>
                  )
                );
              })}
            {!currentLoading &&
              currentAssignments?.length === 0 &&
              showCurrentAssignments && (
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                    <Alert severity="info">
                      {t("noCurrentAssignmentAlert.title")}
                    </Alert>
                  </Grid>
                </Box>
              )}
            {currentLoading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "100%",
                  alignItems: "center",
                }}
              >
                <img src={dots} alt="dots icon" height={50} />
              </Box>
            )}

            {/* display search results */}
            {showSearchResult &&
              searchResult?.length > 0 &&
              searchResult.map((user, key) => {
                const userInPermission = currentAssignments.find(
                  (item) => item.cust_assignedUser === user.userId
                );
                const daysLeft = calculateRemainingDays(
                  userInPermission?.cust_endDate
                );
                return userInPermission ? (
                  <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4 }} key={key}>
                    <EmployeeCard
                      firstName={
                        userInPermission?.cust_assignedUserNav.firstName
                      }
                      lastName={
                        userInPermission?.cust_assignedUserNav?.lastName
                      }
                      department={
                        userInPermission?.cust_assignedUserNav.department
                      }
                      userName={userInPermission?.cust_assignedUser}
                      email={userInPermission?.cust_assignedUserNav.email}
                      token={token}
                      applicationId={applicationId}
                      handleAlert={handleAlert}
                      expiryDate={userInPermission?.cust_endDate}
                      startDate={userInPermission?.effectiveStartDate}
                      handleRender={handleRender}
                      daysLeft={daysLeft}
                    />
                  </Grid>
                ) : (
                  <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4 }} key={key}>
                    <EmployeeCard
                      firstName={user.firstName}
                      lastName={user.lastName}
                      department={user.department}
                      userName={user.username}
                      userId={user.userId}
                      email={user.email}
                      token={token}
                      applicationId={applicationId}
                      handleAlert={handleAlert}
                      handleRender={handleRender}
                    />
                  </Grid>
                );
              })}

            {!showCurrentAssignments && searchResult?.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                  <Alert severity="info">
                    {t("searchForm.noResultMessage")}
                  </Alert>
                </Grid>
              </Box>
            )}
          </Grid>
          {moreResultLoading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                width: "100%",
                alignItems: "center",
              }}
              mt={2}
            >
              <img src={dots} alt="dots icon" height={50} />
            </Box>
          )}
          {showSearchResult &&
            searchResult?.length >=
              FRONTEND?.UserSearch?.defaultUserSearchOptions?.pageSize &&
            !moreResultLoading && (
              <Box mt={5} sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleLoadMoreUser}
                  disabled={moreResultLoading ? true : false}
                >
                  {t("buttons.loadMore")}
                </Button>
              </Box>
            )}
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          <PositionDataTable
            firstName={applicationDetail.firstName}
            lastName={applicationDetail.lastName}
            jobTitle={applicationDetail.externalTitle}
            jobReqId={applicationDetail.jobReqId}
            applicationId={applicationId}
            configuration={FRONTEND}
          />
          <Grid container spacing={4} mt={2} mx={2}>
            {historyLoading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "100%",
                  alignItems: "center",
                }}
                mt={2}
              >
                <img src={dots} alt="dots icon" height={50} />
              </Box>
            )}
            {historyAssignments?.length > 0 ? (
              historyAssignments.map((user, key) => {
                const { firstName, lastName, department, email } =
                  user.cust_assignedUserNav;
                return (
                  <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4 }} key={key}>
                    <EmployeeCard
                      firstName={firstName}
                      lastName={lastName}
                      department={department}
                      userName={user.cust_assignedUser}
                      email={email}
                      token={token}
                      applicationId={applicationId}
                      expiryDate={user.cust_endDate}
                      startDate={user.effectiveStartDate}
                      history
                    />
                  </Grid>
                );
              })
            ) : (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                  <Alert severity="info">{t("noHistoryAlert.title")}</Alert>
                </Grid>
              </Box>
            )}
          </Grid>
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default Permission;
